import { IconAdjustmentsHorizontal, IconCalendar } from "@tabler/icons-react";
import { Trans, useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Indicator,
  Input,
  Popover,
  SegmentedControl,
  Slider,
  Stack,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { Chip } from "./Chip";
import { activeCount, ARCHIVE_DEFAULT, REVIEW_DEFAULT, type Filters } from "../state/prefs";

/**
 * Every way of narrowing a listing, behind one trigger.
 *
 * It replaces two rows of tabs -- six of them over the review queue, three over the archive --
 * which were mutually exclusive for no reason other than being tabs: an editor who wanted the
 * unlinked shows that retention had already auto-approved had to pick one and read the other
 * off the flags. These narrow together, so the same question is one panel.
 *
 * The two modes differ only in what the middle holds. Review has no tracker -- nobody reviews
 * a show by having listened to it -- and the archive has no notion of an unverified row to
 * offer a visitor. The kind and the tombstones are common to both.
 */
export function FilterMenu({
  value,
  onChange,
  review = false,
  tracker = false,
}: {
  value: Filters;
  onChange: (next: Filters) => void;
  /** Review mode: the three flags an editor works through, instead of the tracker. */
  review?: boolean;
  /** Whether the tracker has anything to say. False signed out, where there is none. */
  tracker?: boolean;
}) {
  const { t } = useTranslation();
  const active = activeCount(value, review ? REVIEW_DEFAULT : ARCHIVE_DEFAULT) > 0;
  const set = <K extends keyof Filters>(key: K, next: Filters[K]) =>
    onChange({ ...value, [key]: next });

  return (
    <Popover width={260} position="bottom-start" withinPortal>
      {/* The trigger keeps its own ink and takes a mark instead of a recolour: an accent
          button reads as the primary action of the row it sits in, which a filter trigger
          never is, and beside a red Undo it made the queue's head look like two calls to
          action. Inside the Popover rather than around it, so the Button stays the popover
          target and keeps the aria-haspopup/aria-expanded pair; the dot is decorative. */}
      <Indicator disabled={!active} color="accent" size={8} offset={5} flex="none">
        <Popover.Target>
          <Button
            variant="subtle"
            // The dot alone would leave the state unreadable to anyone who cannot see it, and
            // unreadable in a screenshot. The name carries it too.
            color="var(--mantine-color-text)"
            leftSection={
              <IconAdjustmentsHorizontal
                size={16}
                stroke={1.5}
                aria-hidden="true"
                focusable="false"
              />
            }
            aria-label={active ? t("filterMenu.filterActiveLabel") : t("filterMenu.filter")}
            title={active ? t("filterMenu.filterActiveTitle") : t("filterMenu.filter")}
          >
            {t("filterMenu.filter")}
          </Button>
        </Popover.Target>
      </Indicator>

      <Popover.Dropdown>
        <Stack gap="sm">
          {/* A radio group rather than a row of chips: these are alternatives, and separate
              pills say nothing about being alternatives -- they look exactly like the
              independent toggles further down. */}
          <SegmentedControl
            fullWidth
            aria-label={t("filterMenu.kind")}
            value={value.kind}
            onChange={(kind) => set("kind", kind as Filters["kind"])}
            data={[
              { value: "all", label: t("filterMenu.kindAll") },
              { value: "show", label: t("filterMenu.kindShows") },
              { value: "track", label: t("filterMenu.kindJukebox") },
            ]}
          />

          {!review && tracker && (
            <SegmentedControl
              fullWidth
              aria-label={t("filterMenu.listened")}
              value={value.listened}
              onChange={(listened) => set("listened", listened as Filters["listened"])}
              data={[
                { value: "all", label: t("filterMenu.listenedAll") },
                { value: "yes", label: t("filterMenu.listenedYes") },
                { value: "no", label: t("filterMenu.listenedNo") },
              ]}
            />
          )}

          {/* Only under "Unlistened", because it is the only side with a spread to divide:
              "listened to" is one figure, the 95% the tracker rounds up to 100. */}
          {!review && tracker && value.listened === "no" && (
            <Input.Wrapper
              label={
                <Trans
                  i18nKey="filterMenu.heardAtMost"
                  values={{ percent: value.listenedMax }}
                  components={{ strong: <strong /> }}
                />
              }
            >
              <Slider
                min={0}
                max={99}
                value={value.listenedMax}
                onChange={(percent) => set("listenedMax", percent)}
                label={(percent) => `${percent}%`}
              />
            </Input.Wrapper>
          )}

          {review && (
            <>
              <Checkbox
                label={t("filterMenu.unverified")}
                description={t("filterMenu.unverifiedHint")}
                checked={value.provisional}
                onChange={(event) => set("provisional", event.currentTarget.checked)}
              />
              <Checkbox
                label={t("filterMenu.unlinked")}
                description={t("filterMenu.unlinkedHint")}
                checked={value.unlinked}
                onChange={(event) => set("unlinked", event.currentTarget.checked)}
              />
              <Checkbox
                label={t("filterMenu.autoApproved")}
                description={t("filterMenu.autoApprovedHint")}
                checked={value.auto}
                onChange={(event) => set("auto", event.currentTarget.checked)}
              />
            </>
          )}
          <Checkbox
            label={t("filterMenu.expired")}
            description={t("filterMenu.expiredHint")}
            checked={value.expired}
            onChange={(event) => set("expired", event.currentTarget.checked)}
          />
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

/**
 * The date range, behind a calendar glyph of its own.
 *
 * Separate from the filter panel because it is asked differently: the rest of that panel is
 * browsing ("show me the unlinked ones") while a date is someone who already knows the answer
 * they want ("the show on the 4th"), and a control reached knowingly should not be two clicks
 * behind a panel that opens on a kind selector. The calendar is drawn in the dropdown rather
 * than behind an input in it, so the panel opens on the thing it is for.
 *
 * One range picker rather than a From field and a To field. `@mantine/dates` speaks the same
 * `YYYY-MM-DD` strings `Filters` already stores, so no conversion sits between the two, and a
 * range that runs backwards is unrepresentable rather than merely validated -- the second
 * click always lands on or after the first. `allowSingleDateInRange` because "the show on the
 * 4th" is the common question, and without it the 4th twice selects nothing.
 *
 * The two days are inclusive and read in the displayed zone; `segmentQuery` turns them into
 * the pair of instants the listing filters on.
 */
export function DateMenu({
  value,
  onChange,
}: {
  value: Filters;
  onChange: (next: Filters) => void;
}) {
  const { t } = useTranslation();
  const active = !!(value.from || value.to);

  return (
    <Popover position="bottom-start" withinPortal>
      {/* `flex="none"` moves to the wrapper with the trigger: it is the flex item now, and
          the row it is in has a search field that grows. */}
      <Indicator disabled={!active} color="accent" size={8} offset={5} flex="none">
        <Popover.Target>
          <Button
            variant="subtle"
            color="var(--mantine-color-text)"
            aria-label={active ? t("filterMenu.dateRangeActiveLabel") : t("filterMenu.dateRange")}
            title={active ? t("filterMenu.dateRangeActiveTitle") : t("filterMenu.dateRange")}
          >
            <IconCalendar size={16} stroke={1.5} aria-hidden="true" focusable="false" />
          </Button>
        </Popover.Target>
      </Indicator>

      <Popover.Dropdown>
        <Stack gap="sm" align="center">
          <DatePicker
            type="range"
            allowSingleDateInRange
            // `Filters` spells "no date" as the empty string, because it is persisted and a
            // JSON round trip keeps a string and would have to be taught to keep a null.
            value={[value.from || null, value.to || null]}
            onChange={([from, to]) => onChange({ ...value, from: from ?? "", to: to ?? "" })}
          />
          {/* A half-picked range -- one click in, no end yet -- is already filtering the
              listing, so the way back out has to be visible from inside the panel. */}
          <Chip onClick={() => onChange({ ...value, from: "", to: "" })} disabled={!active}>
            {t("filterMenu.dateClear")}
          </Chip>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
